import * as XLSX from 'xlsx';

// Helper function to check if a date is within fiscal year
const isWithinFiscalYear = (dateStr, fiscalYear) => {
    if (!dateStr || !fiscalYear?.start || !fiscalYear?.end) return false;
    
    try {
        const checkDate = new Date(dateStr);
        const fyStart = new Date(fiscalYear.start);
        const fyEnd = new Date(fiscalYear.end);
        
        return checkDate >= fyStart && checkDate <= fyEnd;
    } catch (error) {
        console.warn('Date parsing error:', dateStr);
        return false;
    }
};

// Get default fiscal year dates if not provided
const getDefaultDates = (fiscalYear) => {
    if (fiscalYear?.start && fiscalYear?.end) {
        return { start: fiscalYear.start, end: fiscalYear.end };
    }
    const currentYear = new Date().getFullYear();
    return { start: `${currentYear}-01-01`, end: `${currentYear}-12-31` };
};

// Get fiscal year label for display
const getFiscalYearLabel = (fiscalYear) => {
    if (fiscalYear?.start && fiscalYear?.end) {
        const startYear = new Date(fiscalYear.start).getFullYear();
        const endYear = new Date(fiscalYear.end).getFullYear();
        if (startYear === endYear) {
            return `${startYear}`;
        }
        return `${startYear} - ${endYear}`;
    }
    const currentYear = new Date().getFullYear();
    return `${currentYear} - ${currentYear + 1}`;
};

/**
 * Creates a styled worksheet with auto-sized columns
 */
const createStyledWorksheet = (data, columnWidths) => {
  const headers = Object.keys(data[0] || {});
  const rows = data.map(row => headers.map(h => row[h] || ''));
  const sheetData = [headers, ...rows];
  
  const ws = XLSX.utils.aoa_to_sheet(sheetData);
  
  if (columnWidths && columnWidths.length > 0) {
    ws['!cols'] = columnWidths;
  }
  
  return ws;
};

/**
 * Auto-sizes columns based on content
 */
const calculateColumnWidths = (data, options = {}) => {
  if (!data || data.length === 0) return [];
  
  const { minWidth = 10, maxWidth = 50 } = options;
  const headers = Object.keys(data[0]);
  
  const widths = headers.map((header) => {
    let maxLength = header.length;
    
    data.forEach(row => {
      const value = row[header] || '';
      const cellLength = String(value).length;
      if (cellLength > maxLength) {
        maxLength = Math.min(cellLength, maxWidth);
      }
    });
    
    let width = maxLength + 2;
    if (width < minWidth) width = minWidth;
    if (width > maxWidth) width = maxWidth;
    
    return { wch: width };
  });
  
  return widths;
};

/**
 * Parse imported objectives from Excel file.
 * Now conditionally includes KPA columns based on visibility setting
 */
export const parseImportedObjectives = async (
  file,
  fiscalYear = null,
  isPastYearMigration = false,
  goals = [],
  managerObjectives = [],
  kraEnabled = true  // KPA visibility flag
) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const defaultDates = getDefaultDates(fiscalYear);
        const currentYear = new Date().getFullYear();

        // Exclude instruction sheets
        const sheetNames = workbook.SheetNames.filter(
          (name) => !name.includes('INSTRUCTION') && !name.includes('READ ME')
        );

        if (sheetNames.length === 0) {
          reject(new Error('No objective sheets found.'));
          return;
        }

        const objectives = [];

        sheetNames.forEach((sheetName) => {
          const sheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });
          if (jsonData.length === 0) return;

          // Find first row with an objective name
          const firstRow = jsonData.find((row) => row['Objective Name']?.trim());
          if (!firstRow) {
            console.warn(`Sheet "${sheetName}" has no objective name, skipping.`);
            return;
          }

          // ----- Metadata from first row -----
          const goalText = firstRow['Company Goal (Category)']?.toString().trim() || '';
          const managerText = firstRow['Manager Objective']?.toString().trim() || '';
          
          let startDate = firstRow['Start Date'] || '';
          let completionDate = firstRow['Completion Date'] || '';

          // Use fiscal year dates if provided
          if (fiscalYear?.start && fiscalYear?.end) {
            const startInFY = isWithinFiscalYear(startDate, fiscalYear);
            const endInFY = isWithinFiscalYear(completionDate, fiscalYear);
            if (!startInFY && !endInFY && startDate && completionDate) {
              startDate = fiscalYear.start;
              completionDate = fiscalYear.end;
            }
            if (!startInFY && startDate) startDate = fiscalYear.start;
            if (!endInFY && completionDate) completionDate = fiscalYear.end;
          }
          if (!startDate) startDate = defaultDates.start;
          if (!completionDate) completionDate = defaultDates.end;

          // ----- Try to match the goal -----
          let matchedGoal = null;
          if (goalText) {
            matchedGoal = goals.find((g) => {
              const full = `${g.name}${g.category ? ` (${g.category})` : ''}`;
              return full === goalText;
            });
            if (!matchedGoal) {
              matchedGoal = goals.find((g) => g.name === goalText);
            }
          }

          // ----- Try to match the manager objective -----
          let matchedManager = null;
          if (managerText) {
            matchedManager = managerObjectives.find((m) => m.name === managerText);
          }

          // ----- Build the objective object -----
          const objective = {
            name: firstRow['Objective Name']?.trim() || '',
            description: firstRow['Description']?.trim() || '',
            weight: parseFloat(firstRow['Objective Weight (%)']) || 0,
            startDate: startDate,
            completionDate: completionDate,
            status: firstRow['Status']?.trim() || 'draft',
            performanceYear: fiscalYear?.start ? new Date(fiscalYear.start).getFullYear() : currentYear,
            kras: [],
            kpis: [],
            _isImported: true,
            _importedFrom: 'past_fy',
            companyAlignedGoal: matchedGoal?.id || '',
            objectiveAlignment: matchedManager?.id || '',
            higherLevelObjectiveId: matchedManager?.id || '',
            higherLevelObjectiveName: matchedManager?.name || '',
          };

          if (matchedManager?.companyAlignedGoal && !objective.companyAlignedGoal) {
            objective.companyAlignedGoal = matchedManager.companyAlignedGoal;
          }

          // ✅ ONLY parse KPAs if kraEnabled is true
          if (kraEnabled) {
            jsonData.forEach((row) => {
              const kraName = row['KPA Name']?.toString().trim() || '';
              const kraDesc = row['KPA Description']?.toString().trim() || '';
              const kraWeight = parseFloat(row['KPA Weight (%)']) || 0;
              
              if (kraName || kraDesc || kraWeight > 0) {
                const existingKra = objective.kras.find(k => k.name === kraName);
                if (!existingKra) {
                  objective.kras.push({
                    name: kraName,
                    description: kraDesc,
                    weight: kraWeight,
                  });
                }
              }
            });
          }

          // ----- Collect KPIs from all rows (ALWAYS visible) -----
          const hasPlural = jsonData.some((r) => r['KPIs Name'] !== undefined);
          const nameCol = hasPlural ? 'KPIs Name' : 'KPI Name';
          const descCol = 'KPI Description';
          const measCol = 'KPI Measure';
          const weightCol = 'KPI Weight (%)';

          jsonData.forEach((row) => {
            const kpiName = row[nameCol]?.toString().trim() || '';
            const kpiDesc = row[descCol]?.toString().trim() || '';
            const kpiMeas = row[measCol]?.toString().trim() || '';
            const kpiWeightRaw = row[weightCol]?.toString().trim() || '';
            const kpiWeight = parseFloat(kpiWeightRaw);
            if (kpiName || kpiDesc || kpiMeas || (kpiWeightRaw !== '' && Number.isFinite(kpiWeight))) {
              objective.kpis.push({
                name: kpiName,
                description: kpiDesc,
                target: kpiMeas,
                weight: Number.isFinite(kpiWeight) ? kpiWeight : '',
              });
            }
          });

          if (objective.name) {
            objectives.push(objective);
          }
        });

        console.log(`✅ Parsed ${objectives.length} objectives`);
        console.log(
          `📊 Total KPIs: ${objectives.reduce((sum, o) => sum + o.kpis.length, 0)}`
        );
        console.log(
          `📋 Total KPAs: ${objectives.reduce((sum, o) => sum + o.kras.length, 0)}`
        );
        console.log(
          `🔗 Auto‑linked ${objectives.filter((o) => o.companyAlignedGoal).length} goals and ${
            objectives.filter((o) => o.objectiveAlignment).length
          } manager objectives.`
        );

        if (objectives.length === 0) {
          reject(new Error('No valid objectives found.'));
        } else {
          resolve(objectives);
        }
      } catch (error) {
        console.error('Parse error:', error);
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Download Objective Template - Conditionally includes KPA columns
 */
export const downloadObjectiveTemplate = (
  fiscalYear,
  goals = [],
  managerObjectives = [],
  kraEnabled = true  // KPA visibility flag
) => {
    const fiscalYearLabel = getFiscalYearLabel(fiscalYear);
    const defaultStartDate = fiscalYear?.start || `${new Date().getFullYear()}-01-01`;
    const defaultEndDate = fiscalYear?.end || `${new Date().getFullYear()}-12-31`;

    const formatDateForDisplay = (dateStr) => {
        if (!dateStr) return 'Not set';
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric' 
            });
        } catch {
            return dateStr;
        }
    };

    const displayStartDate = formatDateForDisplay(defaultStartDate);
    const displayEndDate = formatDateForDisplay(defaultEndDate);

    // ----- INSTRUCTION SHEET -----
    const instructionsData = [
        { 'Step': '📋', 'Instruction': '📌 OBJECTIVE SETTING GUIDE', 'Details': `Use this template to create your performance objectives for FY ${fiscalYearLabel}` },
        { 'Step': '', 'Instruction': '', 'Details': '' },
        { 'Step': '1️⃣', 'Instruction': '📝 SMART Objectives', 'Details': 'Create objectives that are:' },
        { 'Step': '   ', 'Instruction': '✓ S - Specific', 'Details': 'Clearly define what needs to be accomplished' },
        { 'Step': '   ', 'Instruction': '✓ M - Measurable', 'Details': 'Include metrics to track progress and success' },
        { 'Step': '   ', 'Instruction': '✓ A - Achievable', 'Details': 'Set realistic goals that can be accomplished' },
        { 'Step': '   ', 'Instruction': '✓ R - Relevant', 'Details': 'Align with your role, department, and company strategy' },
        { 'Step': '   ', 'Instruction': '✓ T - Time-bound', 'Details': 'Define clear deadlines and milestones' },
        { 'Step': '', 'Instruction': '', 'Details': '' },
        { 'Step': '2️⃣', 'Instruction': '⚖️ Weight Allocation', 'Details': 'Assign weight to each objective. TOTAL MUST EQUAL 100% across ALL objectives.' },
        { 'Step': '3️⃣', 'Instruction': '📊 KPIs (REQUIRED)', 'Details': 'Define measurable KPIs for each objective:' },
        { 'Step': '   ', 'Instruction': '→ KPI Name', 'Details': 'What you are measuring' },
        { 'Step': '   ', 'Instruction': '→ KPI Description', 'Details': 'How it will be measured' },
        { 'Step': '   ', 'Instruction': '→ KPI Measure', 'Details': 'Target value (e.g., 90%, $100K, 24 hours)' },
        { 'Step': '   ', 'Instruction': '→ KPI Weight (%)', 'Details': 'Optional - weight of this KPI within the objective (0-100)' },
    ];

    //  Conditionally add KPA instructions
    if (kraEnabled) {
        instructionsData.push({ 'Step': '', 'Instruction': '', 'Details': '' });
        instructionsData.push(
            { 'Step': '4️⃣', 'Instruction': '📋 KPAs (OPTIONAL)', 'Details': 'Key Performance Areas - high-level areas of focus' },
            { 'Step': '   ', 'Instruction': '→ KPA Name', 'Details': 'Area of focus (e.g., "Customer Service Excellence")' },
            { 'Step': '   ', 'Instruction': '→ KPA Description', 'Details': 'Detailed description of the focus area' },
            { 'Step': '   ', 'Instruction': '→ KPA Weight (%)', 'Details': 'Optional - weight of this KPA within the objective' }
        );
    }

    // Continue with remaining instructions
    instructionsData.push(
        { 'Step': '', 'Instruction': '', 'Details': '' },
        { 'Step': '5️⃣', 'Instruction': '🔗 ALIGNMENT', 'Details': 'Ensure objectives align with:' },
        { 'Step': '   ', 'Instruction': '→ Company Strategy', 'Details': 'Select from the Company Goal dropdown in the system' },
        { 'Step': '   ', 'Instruction': '→ Manager Objectives', 'Details': 'Link to your Line Manager\'s objectives (optional)' },
        { 'Step': '', 'Instruction': '', 'Details': '' },
        { 'Step': '6️⃣', 'Instruction': '📅 Dates (OPTIONAL)', 'Details': `Will use ${displayStartDate} to ${displayEndDate} if left blank` },
    );

    // Add KPA disabled message if not enabled
    if (!kraEnabled) {
        instructionsData.push(
            { 'Step': '', 'Instruction': '', 'Details': '' },
            { 'Step': '🔒', 'Instruction': 'KPA COLUMNS DISABLED', 'Details': 'KPAs (Key Performance Areas) are currently disabled by your administrator. KPA columns are hidden and will be ignored.' }
        );
    }

    // Add available goals and manager objectives (same as before)
    if (goals.length > 0) {
        instructionsData.push({ 'Step': '', 'Instruction': '', 'Details': '' });
        instructionsData.push({ 
            'Step': '🎯', 
            'Instruction': '--- AVAILABLE COMPANY GOALS ---', 
            'Details': '(Copy the full text including category)' 
        });
        goals.slice(0, 8).forEach(g => {
            const fullText = `${g.name}${g.category ? ` (${g.category})` : ''}`;
            instructionsData.push({
                'Step': '',
                'Instruction': fullText,
                'Details': 'Paste into "Company Goal (Category)" column'
            });
        });
        if (goals.length > 8) {
            instructionsData.push({
                'Step': '',
                'Instruction': `... and ${goals.length - 8} more goals available`,
                'Details': 'View all goals in the Goals Management section'
            });
        }
    }

    // Build KPA column headers conditionally
    const getKRAHeaders = () => {
        if (kraEnabled) {
            return [
                'KPA Name',
                'KPA Description',
                'KPA Weight (%)'
            ];
        }
        return [];
    };

    const getKRAColumnWidths = () => {
        if (kraEnabled) {
            return [
                { wch: 25 }, // KPA Name
                { wch: 40 }, // KPA Description
                { wch: 15 }  // KPA Weight (%)
            ];
        }
        return [];
    };

    // Build sheet rows with conditional KPA columns
    const buildObjectiveSheet = (objName, objDesc, objWeight, kpis, kras = [], companyGoal = '', managerObjective = '') => {
        const maxRows = Math.max(kpis.length, kraEnabled ? (kras.length || 1) : 1);
        const rows = [];
        
        for (let i = 0; i < maxRows; i++) {
            const kpi = kpis[i] || { name: '', description: '', target: '', weight: '' };
            const kra = kras[i] || { name: '', description: '', weight: '' };
            
            const row = {
                'Company Goal (Category)': i === 0 ? companyGoal : '',
                'Manager Objective': i === 0 ? managerObjective : '',
                'Objective Name': i === 0 ? objName : '',
                'Description': i === 0 ? objDesc : '',
                'Objective Weight (%)': i === 0 ? objWeight : '',
                'Start Date': i === 0 ? defaultStartDate : '',
                'Completion Date': i === 0 ? defaultEndDate : '',
                'Status': i === 0 ? 'draft' : '',
            };

            // ✅ Conditionally add KPA columns
            if (kraEnabled) {
                row['KPA Name'] = kra.name || '';
                row['KPA Description'] = kra.description || '';
                row['KPA Weight (%)'] = kra.weight !== '' ? kra.weight : '';
            }

            // Add KPI columns (always)
            row['KPIs Name'] = kpi.name || '';
            row['KPI Description'] = kpi.description || '';
            row['KPI Measure'] = kpi.target || '';
            row['KPI Weight (%)'] = kpi.weight !== '' && kpi.weight !== undefined && kpi.weight !== null ? kpi.weight : '';

            rows.push(row);
        }
        
        // Add one blank row for extra entries
        const blankRow = {
            'Company Goal (Category)': '',
            'Manager Objective': '',
            'Objective Name': '', 
            'Description': '',
            'Objective Weight (%)': '',
            'Start Date': '',
            'Completion Date': '',
            'Status': '',
        };
        
        if (kraEnabled) {
            blankRow['KPA Name'] = '';
            blankRow['KPA Description'] = '';
            blankRow['KPA Weight (%)'] = '';
        }
        
        blankRow['KPIs Name'] = '';
        blankRow['KPI Description'] = '';
        blankRow['KPI Measure'] = '';
        blankRow['KPI Weight (%)'] = '';
        
        rows.push(blankRow);
        return rows;
    };

    const wb = XLSX.utils.book_new();
    
    // Build column widths
    const baseColWidths = [
        { wch: 35 }, // Company Goal (Category)
        { wch: 30 }, // Manager Objective
        { wch: 30 }, // Objective Name
        { wch: 40 }, // Description
        { wch: 15 }, // Weight
        { wch: 12 }, // Start Date
        { wch: 12 }, // Completion Date
        { wch: 10 }, // Status
    ];
    
    const kraColWidths = getKRAColumnWidths();
    const kpiColWidths = [
        { wch: 25 }, // KPIs Name
        { wch: 40 }, // KPI Description
        { wch: 20 }, // KPI Measure
        { wch: 15 }  // KPI Weight (%)
    ];
    
    const colWidths = [...baseColWidths, ...kraColWidths, ...kpiColWidths];

    // Example KPIs and KPAs
    const kpis1 = [
        { name: 'KPI 1 Name', description: 'Description of how KPI 1 is measured', target: 'Target value for KPI 1', weight: 50 },
        { name: 'KPI 2 Name', description: 'Description of how KPI 2 is measured', target: 'Target value for KPI 2', weight: 50 }
    ];
    const kras1 = kraEnabled ? [
        { name: 'KPA 1 Name', description: 'Description of KPA 1', weight: 50 },
        { name: 'KPA 2 Name', description: 'Description of KPA 2', weight: 50 }
    ] : [];

    const exampleGoal1 = goals.length > 0 ? `${goals[0].name}${goals[0].category ? ` (${goals[0].category})` : ''}` : '';
    const exampleManager1 = managerObjectives.length > 0 ? managerObjectives[0].name : '';

    // Instructions sheet
    const instructionsWs = XLSX.utils.json_to_sheet(instructionsData);
    instructionsWs['!cols'] = [
        { wch: 6 },
        { wch: 50 },
        { wch: 60 }
    ];
    instructionsWs['!rows'] = instructionsData.map(() => ({ hpt: 22 }));
    XLSX.utils.book_append_sheet(wb, instructionsWs, '📌 INSTRUCTIONS');

    // Example sheets
    const rows1 = buildObjectiveSheet(
        'Example Objective 1',
        'Provide a clear description of what this objective aims to achieve.',
        50,
        kpis1,
        kras1,
        exampleGoal1,
        exampleManager1
    );
    const sheet1Ws = XLSX.utils.json_to_sheet(rows1);
    sheet1Ws['!cols'] = colWidths;
    sheet1Ws['!freeze'] = { xSplit: 0, ySplit: 1 };
    XLSX.utils.book_append_sheet(wb, sheet1Ws, 'Example Objective 1');

    // Blank template
    const blankRows = buildObjectiveSheet('', '', '', [{ name: '', description: '', target: '', weight: '' }], [], '', '');
    const blankWs = XLSX.utils.json_to_sheet(blankRows);
    blankWs['!cols'] = colWidths;
    blankWs['!freeze'] = { xSplit: 0, ySplit: 1 };
    XLSX.utils.book_append_sheet(wb, blankWs, 'Blank Template');

    const fileName = `objectives_template_${fiscalYearLabel.replace(' - ', '_')}.xlsx`;
    XLSX.writeFile(wb, fileName);
};

/**
 * Export past year objectives - Conditionally includes KPA columns
 */
export const exportPastYearObjectivesToExcel = (
    pastYearObjectives,
    fiscalYear,
    goals = [],
    managerObjectives = [],
    kraEnabled = true  // KPA visibility flag
) => {
    const fiscalYearLabel = getFiscalYearLabel(fiscalYear);
    const defaultStartDate = fiscalYear?.start || `${new Date().getFullYear()}-01-01`;
    const defaultEndDate = fiscalYear?.end || `${new Date().getFullYear()}-12-31`;

    const instructionsData = [
        { 'Step': '📋', 'Instruction': '📌 PAST YEAR OBJECTIVE MIGRATION', 'Details': `These objectives are from previous fiscal years and need to be re-linked for ${fiscalYearLabel}` },
        { 'Step': '', 'Instruction': '', 'Details': '' },
        { 'Step': '1️⃣', 'Instruction': '🔗 RE-LINK GOALS', 'Details': 'Paste the exact goal text from "Current Company Goals" below into the "Company Goal (Category)" column' },
        { 'Step': '2️⃣', 'Instruction': '👥 RE-LINK MANAGERS (Optional)', 'Details': 'Paste manager objective name into "Manager Objective" column if needed' },
        { 'Step': '3️⃣', 'Instruction': '📊 REVIEW KPIs & KPAs', 'Details': 'KPIs are preserved. KPAs are preserved only if enabled.' },
        { 'Step': '4️⃣', 'Instruction': '⚖️ CHECK WEIGHTS', 'Details': `All objectives must total 100% for ${fiscalYearLabel}` },
        { 'Step': '5️⃣', 'Instruction': '📤 IMPORT', 'Details': 'Use the "Import Objectives" button in the system' },
    ];

    if (!kraEnabled) {
        instructionsData.push(
            { 'Step': '', 'Instruction': '', 'Details': '' },
            { 'Step': '🔒', 'Instruction': 'KPA COLUMNS DISABLED', 'Details': 'KPAs (Key Performance Areas) are currently disabled by your administrator. KPA data will be excluded.' }
        );
    }

    // Build column widths conditionally
    const baseColWidths = [
        { wch: 35 }, // Company Goal (Category)
        { wch: 30 }, // Manager Objective
        { wch: 30 }, // Objective Name
        { wch: 40 }, // Description
        { wch: 15 }, // Weight
        { wch: 12 }, // Start Date
        { wch: 12 }, // Completion Date
        { wch: 10 }, // Status
    ];
    
    const kraColWidths = kraEnabled ? [
        { wch: 25 }, // KPA Name
        { wch: 40 }, // KPA Description
        { wch: 15 }  // KPA Weight (%)
    ] : [];
    
    const kpiColWidths = [
        { wch: 25 }, // KPIs Name
        { wch: 40 }, // KPI Description
        { wch: 20 }, // KPI Measure
        { wch: 15 }  // KPI Weight (%)
    ];
    
    const colWidths = [...baseColWidths, ...kraColWidths, ...kpiColWidths];

    const wb = XLSX.utils.book_new();

    // Instructions
    const instructionsWs = XLSX.utils.json_to_sheet(instructionsData);
    instructionsWs['!cols'] = [
        { wch: 6 },
        { wch: 45 },
        { wch: 60 }
    ];
    instructionsWs['!rows'] = instructionsData.map(() => ({ hpt: 22 }));
    XLSX.utils.book_append_sheet(wb, instructionsWs, '📌 READ ME');

    // One sheet per past objective
    pastYearObjectives.forEach((obj, idx) => {
        const kpis = obj.kpis?.length ? obj.kpis : [{ name: '', description: '', target: '', weight: '' }];
        const kras = (kraEnabled && obj.kras?.length) ? obj.kras : [{ name: '', description: '', weight: '' }];
        const maxRows = Math.max(kpis.length, kras.length);
        const rows = [];
        
        for (let i = 0; i < maxRows; i++) {
            const kpi = kpis[i] || { name: '', description: '', target: '', weight: '' };
            const kra = kras[i] || { name: '', description: '', weight: '' };
            
            const row = {
                'Company Goal (Category)': i === 0 ? '' : '',
                'Manager Objective': i === 0 ? '' : '',
                'Objective Name': i === 0 ? (obj.name || '') : '',
                'Description': i === 0 ? (obj.description || '') : '',
                'Objective Weight (%)': i === 0 ? (obj.weight || 0) : '',
                'Start Date': i === 0 ? (fiscalYear?.start || obj.startDate || defaultStartDate) : '',
                'Completion Date': i === 0 ? (fiscalYear?.end || obj.completionDate || defaultEndDate) : '',
                'Status': i === 0 ? 'draft' : '',
            };

            if (kraEnabled) {
                row['KPA Name'] = kra.name || '';
                row['KPA Description'] = kra.description || '';
                row['KPA Weight (%)'] = kra.weight !== '' ? kra.weight : '';
            }

            row['KPIs Name'] = kpi.name || '';
            row['KPI Description'] = kpi.description || '';
            row['KPI Measure'] = kpi.target || '';
            row['KPI Weight (%)'] = kpi.weight !== '' && kpi.weight !== undefined && kpi.weight !== null ? kpi.weight : '';

            rows.push(row);
        }

        const ws = XLSX.utils.json_to_sheet(rows);
        ws['!cols'] = colWidths;
        ws['!freeze'] = { xSplit: 0, ySplit: 1 };

        let sheetName = obj.name ? obj.name.substring(0, 31) : `Objective ${idx + 1}`;
        sheetName = sheetName.replace(/[\\/*?:\[\]]/g, '');
        if (wb.SheetNames.includes(sheetName)) {
            sheetName = `${sheetName.substring(0, 27)} ${idx + 1}`;
        }
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    const fileName = `past_fy_migration_to_${fiscalYearLabel.replace(' - ', '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
};